import React from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import DropdownMenu, {
	DropdownItem,
	DropdownItemGroup,
	DropdownItemRadio,
} from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import ThemeIcon from '@atlaskit/icon/core/theme';
import Image from '@atlaskit/image';
import { ButtonItem } from '@atlaskit/menu';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import DarkModeThumbnail from '../../assets/dark-mode.svg';
import LightModeThumbnail from '../../assets/light-mode.svg';
import MatchBrowserThumbnail from '../../assets/match-browser.svg';

import type { ThemeOption, ThemeSwitcherProps } from './types';

const messages = defineMessages({
	appDisplayMode: {
		id: 'design-system.theme-switcher.app-display-mode',
		defaultMessage: 'App display mode',
		description: 'Header for the theme switcher menu options',
	},
	themeButton: {
		id: 'design-system.theme-switcher.theme-button',
		defaultMessage: 'Theme',
		description: 'Label for button to switch to a different theme',
	},
	appearanceButton: {
		id: 'design-system.theme-switcher.appearance-button',
		defaultMessage: 'Appearance',
		description: 'Label for button to switch appearance settings',
	},
	light: {
		id: 'design-system.theme-switcher.light.option',
		defaultMessage: 'Light',
		description: 'Label for the theme switcher light option',
	},
	dark: {
		id: 'design-system.theme-switcher.dark.option',
		defaultMessage: 'Dark',
		description: 'Label for the theme switcher dark option',
	},
	matchBrowser: {
		id: 'design-system.theme-switcher.match-browser.option',
		defaultMessage: 'Match browser',
		description: 'Label for the theme switcher match browser option',
	},
	matchSystem: {
		id: 'design-system.theme-switcher.match-system.option',
		defaultMessage: 'Match system',
		description: 'Label for the theme switcher match system option',
	},
	syncedAcrossApps: {
		id: 'design-system.theme-switcher.synced-across-apps',
		defaultMessage: 'Synced across apps',
		description:
			'Indicates the theme option is synced with global account settings across all apps',
	},
	personalize: {
		id: 'design-system.theme-switcher.personalize',
		defaultMessage: 'Customize display settings',
		description: 'Link to personalize settings in the theme switcher menu',
	},
});

const themeOptions: readonly ThemeOption[] = [
	{
		label: <FormattedMessage {...messages.light} />,
		value: 'light',
		thumbnail: LightModeThumbnail,
	},
	{
		label: <FormattedMessage {...messages.dark} />,
		value: 'dark',
		thumbnail: DarkModeThumbnail,
	},
	{
		label: <FormattedMessage {...messages.matchBrowser} />,
		value: 'auto',
		thumbnail: MatchBrowserThumbnail,
	},
] as const;

const themeOptionsAutoFirst: readonly ThemeOption[] = [
	{
		label: <FormattedMessage {...messages.matchSystem} />,
		value: 'auto',
		thumbnail: MatchBrowserThumbnail,
	},
	{
		label: <FormattedMessage {...messages.light} />,
		value: 'light',
		thumbnail: LightModeThumbnail,
	},
	{
		label: <FormattedMessage {...messages.dark} />,
		value: 'dark',
		thumbnail: DarkModeThumbnail,
	},
] as const;

/**
 * ThemeSwitcher is a dropdown menu component for users to select between light, dark, and auto (match browser) color mode themes.
 */
export const ThemeSwitcher = ({
	testId,
	onThemeChange,
	currentColorMode,
	accountColorMode,
	appearancePreferencesLink,
	onOpenChange,
	shouldInvokeOnOpenChangeOnFocusAndMouseEnter = true,
}: ThemeSwitcherProps): React.JSX.Element => {
	const intl = useIntl();
	const options = fg('shared-user-preferences') ? themeOptionsAutoFirst : themeOptions;
	const shouldInvokeOnOpenChangeOnFocusAndMouseEnterWithCallback =
		shouldInvokeOnOpenChangeOnFocusAndMouseEnter && Boolean(onOpenChange);

	return (
		<>
			{options.map(({ value, thumbnail }) => (
				<link rel="preload" href={thumbnail} as="image" key={value} />
			))}

			<DropdownMenu
				testId={testId}
				placement="auto-start"
				onOpenChange={onOpenChange}
				trigger={({ triggerRef, ...props }) => {
					const handleOpenChangeOnFocusOrMouseEnter = (
						event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>,
					) => {
						if (!shouldInvokeOnOpenChangeOnFocusAndMouseEnterWithCallback) {
							return;
						}

						onOpenChange?.({
							isOpen: true,
							event: event.nativeEvent,
						});
					};

					return (
						<span
							onMouseEnter={handleOpenChangeOnFocusOrMouseEnter}
							onFocus={handleOpenChangeOnFocusOrMouseEnter}
						>
							<ButtonItem
								ref={triggerRef}
								onClick={(event) => event.stopPropagation()}
								iconBefore={<ThemeIcon color="currentColor" label="" />}
								iconAfter={<ChevronRightIcon color="currentColor" label="" size="small" />}
								{...props}
							>
								<FormattedMessage
									{...(fg('shared-user-preferences') ? messages.appearanceButton : messages.themeButton)}
								/>
							</ButtonItem>
						</span>
					);
				}}
				shouldRenderToParent={false}
			>
				<DropdownItemGroup
					title={fg('shared-user-preferences') ? intl.formatMessage(messages.appDisplayMode) : undefined}
				>
					{options.map(({ label, value, thumbnail }) => {
						const isSynced = accountColorMode !== undefined && accountColorMode === value;
						const isSelected = currentColorMode === value;

						return (
							<DropdownItemRadio
								testId={`${testId}--radio-${value}`}
								id={value}
								key={value}
								onClick={() => onThemeChange(value)}
								isSelected={isSelected}
							>
								<Inline space="space.150" alignBlock="center">
									<Image src={thumbnail} width={64} height={48} alt="" />
									<Stack space="space.025">
										{label}
										{isSynced && (
											<Text size="small" color="color.text.subtlest">
												<FormattedMessage {...messages.syncedAcrossApps} />
											</Text>
										)}
									</Stack>
								</Inline>
							</DropdownItemRadio>
						);
					})}
				</DropdownItemGroup>

				{appearancePreferencesLink && (
					<DropdownItemGroup hasSeparator>
						<DropdownItem
							href={appearancePreferencesLink}
							target="_blank"
							rel="noopener"
							testId={testId && `${testId}--personalize`}
							elemAfter={<LinkExternalIcon color="currentColor" label="" size="small" />}
						>
							<FormattedMessage {...messages.personalize} />
						</DropdownItem>
					</DropdownItemGroup>
				)}
			</DropdownMenu>
		</>
	);
};

export default ThemeSwitcher;
